import React, { useEffect, useMemo, useRef, useState } from "react";
import type { WindowBucket } from "~/features/orders/types-order";
import type { TransferQueueRecord } from "~/features/transfer-queue/types";
import { useDeliveryWindows } from "~/shared/contexts/DeliveryWindowsContext";
import { useAutoGrow } from "~/shared/hooks/useAutoGrow";
import { formatWindowName, replaceNamedWindowText, type WindowLike } from "~/shared/utils/delivery-window";
import {
    WindowChoiceFields,
    getInitialWindowChoice,
    getWindowChoiceWindow,
    isWindowChoiceIncomplete,
    type WindowChoice,
} from "~/features/orders/components/WindowChoiceFields";

const transferQueueOutcomeOptions = [
    { value: 'answered', label: 'Answered' },
    { value: 'rescheduled', label: 'Rescheduled' },
    { value: 'alternate_window', label: 'Alternate window' },
    { value: 'confirmed_with_voicemail', label: 'Confirmed with Voicemail' },
    { value: 'confirmed_with_no_answer', label: 'Confirmed with No answer' },
    { value: 'confirmed_through_transfer', label: 'Confirmed through Transfer' },
    { value: 'incorrect_phone_number', label: 'Incorrect phone number' },
    { value: 'cancelled', label: 'Cancelled' },
] as const;

// If the stored outcome is not in our dropdown, default to "answered"
// unanswered and call_failed are auto-set by the system; treat as answered for dispatcher editing
function normalizeOutcomeForEdit(outcome: string | undefined): string {
    if (!outcome) return 'answered';
    if (outcome === 'unanswered' || outcome === 'call_failed') return 'answered';
    const valid = transferQueueOutcomeOptions.map(o => o.value) as string[];
    return valid.includes(outcome) ? outcome : 'answered';
}

type Option = { value: string; label: string };

// Same custom dropdown as schedule edit dialog: button with ▼ chevron and custom list
function InlineSelect({
    value,
    options,
    onChange,
    disabled,
}: {
    value: string;
    options: readonly Option[];
    onChange: (next: string) => void;
    disabled?: boolean;
}) {
    const [open, setOpen] = React.useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const listRef = useRef<HTMLUListElement | null>(null);

    const selectedLabel = useMemo(() => {
        return options.find(o => o.value === value)?.label ?? value;
    }, [options, value]);

    useEffect(() => {
        if (!open) return;
        function onDocMouseDown(e: MouseEvent) {
            if (containerRef.current && containerRef.current.contains(e.target as Node)) return;
            setOpen(false);
        }
        function onKeyDown(e: KeyboardEvent) {
            if (e.key === "Escape") setOpen(false);
        }
        document.addEventListener("mousedown", onDocMouseDown, true);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onDocMouseDown, true);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        listRef.current?.focus();
    }, [open]);

    return (
        <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
            <button
                ref={buttonRef}
                type="button"
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen(v => !v)}
                style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "10px 36px 10px 12px",
                    fontSize: 14,
                    borderRadius: 6,
                    border: "1px solid var(--border)",
                    background: "white",
                    ...(disabled && { opacity: 0.7, cursor: "not-allowed" }),
                }}
            >
                {selectedLabel}
                <span
                    style={{
                        position: "absolute",
                        right: 12,
                        top: "50%",
                        transform: open ? "translateY(-50%) rotate(180deg)" : "translateY(-50%)",
                        pointerEvents: "none",
                        fontSize: 12,
                        color: "var(--text-muted)",
                        transition: "transform 0.15s ease",
                    }}
                    aria-hidden
                >
                    ▼
                </span>
            </button>

            {open && !disabled && (
                <ul
                    ref={listRef}
                    tabIndex={-1}
                    role="listbox"
                    aria-activedescendant={value}
                    style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: "calc(100% + 6px)",
                        margin: 0,
                        padding: 6,
                        listStyle: "none",
                        background: "white",
                        border: "1px solid var(--border)",
                        borderRadius: 8,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                        maxHeight: 220,
                        overflowY: "auto",
                        zIndex: 2000,
                        outline: "none",
                    }}
                >
                    {options.map((opt) => {
                        const isSelected = opt.value === value;
                        const handleSelect = (e: React.MouseEvent | React.KeyboardEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onChange(opt.value);
                            setTimeout(() => {
                                setOpen(false);
                                buttonRef.current?.focus();
                            }, 0);
                        };
                        const handleKeyDown = (e: React.KeyboardEvent) => {
                            if (e.key === "Enter" || e.key === " ") handleSelect(e);
                        };
                        return (
                            <li
                                key={opt.value}
                                role="option"
                                aria-selected={isSelected}
                                tabIndex={0}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={handleSelect}
                                onKeyDown={handleKeyDown}
                                style={{
                                    cursor: "pointer",
                                    background: isSelected ? "var(--neutral-gray)" : "transparent",
                                    borderRadius: 6,
                                }}
                            >
                                <span style={{ display: "block", padding: "8px 10px", fontSize: 14 }}>
                                    {opt.label}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

type TransferQueueEditModalProps = Readonly<{
    record: TransferQueueRecord;
    onClose: () => void;
    onSave: (updates: {
        outcome: string;
        deliveryDate: string;
        window: WindowBucket | null;
        comments: string;
        notes: string;
    }) => void;
    /** Vertical offset (px) from top of modal for the left-pointing tail so it aligns with the selected row. */
    tailTop?: number | null;
}>;

function getRecordDeliveryDateForInput(record: TransferQueueRecord): string {
    const raw = record.updatedDate ?? record.deliveryDateAndWindow?.newDate;
    if (!raw) return '';
    try {
        const d = new Date(raw);
        return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
    } catch {
        return '';
    }
}

function getRecordWindowValue(record: TransferQueueRecord): WindowLike | undefined {
    return record.updatedWindow ?? record.deliveryDateAndWindow?.window;
}

const DEFAULT_TAIL_TOP = 28;
const TAIL_HEIGHT = 24;
const TAIL_INSET = 8;

export function TransferQueueEditModal({
    record,
    onClose,
    onSave,
    tailTop,
}: TransferQueueEditModalProps) {
    const rootRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(0);

    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => {
            setHeight(el.offsetHeight);
        });
        ro.observe(el);
        setHeight(el.offsetHeight);
        return () => ro.disconnect();
    }, []);

    const rawTop = tailTop != null ? tailTop : DEFAULT_TAIL_TOP;
    const topPx = height > 0
        ? Math.max(TAIL_INSET, Math.min(height - TAIL_HEIGHT - TAIL_INSET, rawTop))
        : rawTop;
    const { presets } = useDeliveryWindows();

    const initialWindow = getRecordWindowValue(record);

    // Helper function to format date for date input (YYYY-MM-DD)
    const formatDateForInput = (date: string | Date | undefined | null): string => {
        if (!date) return '';
        // If already in YYYY-MM-DD format, return as-is
        if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return date;
        }
        // Otherwise, try to format it
        try {
            const dateObj = typeof date === 'string' ? new Date(date) : date;
            if (isNaN(dateObj.getTime())) return '';
            return dateObj.toISOString().split('T')[0];
        } catch {
            return '';
        }
    };

    const [outcome, setOutcome] = React.useState<string>(() => normalizeOutcomeForEdit(record.outcome));
    const [deliveryDate, setDeliveryDate] = React.useState(
        getRecordDeliveryDateForInput(record) || formatDateForInput(record.deliveryDateAndWindow?.newDate)
    );
    const dateTypingRef = useRef(false);
    // Window: a preset, or a custom From/To range. Starts from the record's window (a matching preset,
    // otherwise its custom range); with no window it defaults to the first preset, as before.
    const [windowChoice, setWindowChoice] = React.useState<WindowChoice | null>(() =>
        getInitialWindowChoice(initialWindow, presets)
    );
    // Presets may arrive after the panel opens
    useEffect(() => {
        if (windowChoice === null && presets.length > 0) setWindowChoice(getInitialWindowChoice(initialWindow, presets));
    }, [windowChoice, presets, initialWindow]);
    const selectedWindow = getWindowChoiceWindow(windowChoice, presets);
    const [notes, setNotes] = React.useState(record.notes ?? '');
    const notesRef = useAutoGrow(notes);

    // Confirmed Date & Window: only "Alternate window" keeps special rules (date disabled, window enabled).
    // For all other outcomes, both Delivery Date and Window are enabled.
    const isDeliveryDateDisabled = outcome === 'alternate_window';
    const isWindowDisabled = false;

    // Notes over 250 chars: red border and disable Save (same as Scheduled modal).
    const notesLength = notes.replace(/\r\n/g, '\n').length;
    const notesOverLimit = notesLength > 250;
    const saveDisabled =
        notesOverLimit ||
        (!isDeliveryDateDisabled && !deliveryDate.trim()) ||
        (isDeliveryDateDisabled && !selectedWindow) ||
        // A half-filled custom window must not be silently dropped
        isWindowChoiceIncomplete(windowChoice);

    // Restrict delivery date to today, tomorrow, and day-after-tomorrow (local calendar days).
    const minDeliveryDate = useMemo(() => {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }, []);

    const maxDeliveryDate = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() + 2);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }, []);

    const handleSave = () => {
        const window: WindowBucket | null = selectedWindow;

        // Comments default: the window time range; an old window name in saved comments becomes its time range
        const windowLabel = formatWindowName(window);
        const commentsValue = outcome === 'cancelled' ? '' : (replaceNamedWindowText(record.comments).trim() || windowLabel);
        onSave({
            outcome,
            deliveryDate,
            window,
            comments: commentsValue,
            notes: notes.slice(0, 250),
        });
        onClose();
    };

    return (
        <div
            ref={rootRef}
            className="modal-panel-enter"
            style={{
                position: 'relative',
                background: 'white',
                borderRadius: 8,
                border: '1px solid var(--border)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                height: 'fit-content',
                maxHeight: 'calc(100vh - 200px)',
                overflow: 'visible',
            }}
        >
            {/* Tail (arrowhead pointing at row) */}
            <div
                style={{
                    position: 'absolute',
                    top: topPx,
                    left: -11,
                    width: 0,
                    height: 0,
                    borderTop: '12px solid transparent',
                    borderBottom: '12px solid transparent',
                    borderRight: '12px solid var(--border)',
                    zIndex: 2,
                }}
            />
            <div
                style={{
                    position: 'absolute',
                    top: topPx + 1,
                    left: -10,
                    width: 0,
                    height: 0,
                    borderTop: '11px solid transparent',
                    borderBottom: '11px solid transparent',
                    borderRight: '11px solid white',
                    zIndex: 3,
                }}
            />
            {/* Header with close button — same as schedule dialog */}
            <div
                style={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    padding: '12px 16px 8px',
                    paddingRight: 24,
                    background: 'white',
                    borderBottom: '1px solid var(--border)',
                }}
            >
                <button
                    onClick={onClose}
                    style={{
                        background: 'white',
                        border: '1px solid var(--border)',
                        borderRadius: '50%',
                        width: 28,
                        height: 28,
                        fontSize: 18,
                        cursor: 'pointer',
                        color: 'var(--text-muted)',
                        padding: 0,
                        lineHeight: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    }}
                    aria-label="Close"
                >
                    ×
                </button>
            </div>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                <div style={{ padding: 16, display: 'grid', gap: 8, overflow: 'auto', flex: 1, minHeight: 0 }}>
                    {/* Editable fields */}
                    <div style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>Outcome</span>
                        <InlineSelect
                            value={outcome}
                            options={transferQueueOutcomeOptions}
                            onChange={(next) => setOutcome(next)}
                        />
                    </div>

                    <div style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>Delivery Date</span>
                        <input
                            type="date"
                            value={deliveryDate}
                            onKeyDown={() => { dateTypingRef.current = true; }}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (!v) {
                                    if (!dateTypingRef.current) setDeliveryDate('');
                                    dateTypingRef.current = false;
                                    return;
                                }
                                dateTypingRef.current = false;
                                const parts = v.split('-');
                                let sanitized = v;
                                if (parts.length === 3) {
                                    const [y, m, d] = parts;
                                    sanitized = `${y}-${m === '00' ? '01' : m}-${d === '00' ? '01' : d}`;
                                }
                                if (sanitized < minDeliveryDate) { setDeliveryDate(minDeliveryDate); return; }
                                if (sanitized > maxDeliveryDate) { setDeliveryDate(maxDeliveryDate); return; }
                                setDeliveryDate(sanitized);
                            }}
                            onBlur={(e) => {
                                const v = e.target.value;
                                if (!v) return;
                                if (v < minDeliveryDate) setDeliveryDate(minDeliveryDate);
                                else if (v > maxDeliveryDate) setDeliveryDate(maxDeliveryDate);
                            }}
                            disabled={isDeliveryDateDisabled}
                            min={minDeliveryDate}
                            max={maxDeliveryDate}
                            style={{
                                padding: '10px 12px',
                                fontSize: 14,
                                borderRadius: 6,
                                border: '1px solid var(--border)',
                                outline: 'none',
                                ...(isDeliveryDateDisabled && { opacity: 0.7, cursor: 'not-allowed' }),
                            }}
                        />
                    </div>

                    <WindowChoiceFields
                        value={windowChoice}
                        onChange={setWindowChoice}
                        presets={presets}
                        disabled={isWindowDisabled}
                        windowLabel="Window"
                        labelGap={4}
                    />

                    <label style={{ display: 'grid', gap: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>Notes</span>
                        <textarea
                            ref={notesRef}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value.replace(/\r\n/g, '\n'))}
                            style={{
                                padding: '10px 12px',
                                fontSize: 14,
                                borderRadius: 6,
                                border: notesOverLimit ? '2px solid #dc2626' : '1px solid var(--border)',
                                outline: 'none',
                                ...(notesOverLimit && { boxShadow: '0 0 0 2px rgba(220, 38, 38, 0.35)' }),
                                minHeight: 60,
                                resize: 'none',
                                overflow: 'hidden',
                                whiteSpace: 'pre-wrap',
                            }}
                            placeholder="Enter notes..."
                        />
                        <span style={{ fontSize: 12, textAlign: 'right', color: notesOverLimit ? '#dc2626' : 'var(--text-muted)' }}>
                            {notesLength} / 250
                        </span>
                    </label>
                </div>

                <div style={{ padding: 16, borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                        className="secondary"
                        onClick={onClose}
                        style={{ padding: '10px 20px' }}
                    >
                        Cancel
                    </button>
                    <button
                        className="primary"
                        onClick={handleSave}
                        disabled={saveDisabled}
                        style={{
                            padding: '10px 20px',
                            ...(saveDisabled && { opacity: 0.6, cursor: 'not-allowed' }),
                        }}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}
